import { z } from 'zod';

// ============================================================================
// ENUMS
// ============================================================================

export type DateRangeType = 'today' | 'this_week' | 'this_month' | 'last_month' | 'custom';
export type TransactionTypeFilter = 'all' | 'income' | 'expense';
export type StatusFilter = 'all' | 'paid' | 'pending' | 'overdue';
export type GroupByType = null | 'category' | 'cost_center' | 'client' | 'supplier' | 'month' | 'week';
export type ComparisonType = 'previous_period' | 'same_period_last_year';
export type MetricType = 'sum' | 'avg' | 'count' | 'min' | 'max';
export type VisualizationType = 'table' | 'bar' | 'line' | 'pie' | 'cards';
export type DataSourceType = 'transactions' | 'categories' | 'cost_centers' | 'clients' | 'suppliers';

// ============================================================================
// ZOD SCHEMAS FOR VALIDATION
// ============================================================================

// Date range schema
export const DateRangeSchema = z.object({
  type: z.enum(['today', 'this_week', 'this_month', 'last_month', 'custom']),
  start: z.string().optional(),
  end: z.string().optional(),
}).refine(
  (data) => data.type !== 'custom' || (data.start && data.end),
  { message: "Custom date range requires both start and end dates" }
);

export type DateRange = z.infer<typeof DateRangeSchema>;

// Filters schema
export const ReportFiltersSchema = z.object({
  transactionType: z.enum(['all', 'income', 'expense']),
  status: z.enum(['all', 'paid', 'pending', 'overdue']).optional(),
  categoryIds: z.array(z.string().uuid()).optional(),
  costCenterIds: z.array(z.string().uuid()).optional(),
  clientIds: z.array(z.string().uuid()).optional(),
  supplierIds: z.array(z.string().uuid()).optional(),
  paymentAccountIds: z.array(z.string().uuid()).optional(),
});

export type ReportFilters = z.infer<typeof ReportFiltersSchema>;

// Comparison schema
export const ComparisonSchema = z.object({
  enabled: z.boolean().default(false),
  type: z.enum(['previous_period', 'same_period_last_year']).optional(),
});

export type Comparison = z.infer<typeof ComparisonSchema>;

// Metrics schema
export const MetricsSchema = z.object({
  amount: z.enum(['sum', 'avg', 'count', 'min', 'max']).default('sum'),
});

export type Metrics = z.infer<typeof MetricsSchema>;

// Chart config schema
export const ChartConfigSchema = z.object({
  showLegend: z.boolean().default(true),
  showDataLabels: z.boolean().default(true),
});

export type ChartConfig = z.infer<typeof ChartConfigSchema>;

// Full report config schema
export const ReportConfigSchema = z.object({
  dataSources: z.array(z.enum(['transactions', 'categories', 'cost_centers', 'clients', 'suppliers'])),
  dateRange: DateRangeSchema,
  filters: ReportFiltersSchema,
  groupBy: z.enum(['category', 'cost_center', 'client', 'supplier', 'month', 'week']).nullable(),
  compareWith: ComparisonSchema,
  metrics: MetricsSchema,
  columns: z.array(z.string()).optional(),
  visualizationType: z.enum(['table', 'bar', 'line', 'pie', 'cards']),
  chartConfig: ChartConfigSchema.optional(),
});

export type ReportConfig = z.infer<typeof ReportConfigSchema>;

// ============================================================================
// CUSTOM REPORT INTERFACES
// ============================================================================

export interface CustomReport {
  id: string;
  company_id: string;
  created_by: string;
  name: string;
  description: string | null;
  is_favorite: boolean;
  folder_id: string | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  config?: CustomReportConfig;
}

export interface CustomReportConfig {
  id: string;
  report_id: string;
  config: ReportConfig;
  created_at: string;
}

export interface CustomReportFolder {
  id: string;
  company_id: string;
  name: string;
  parent_id: string | null;
  created_at: string;
}

// ============================================================================
// FORM DATA TYPES
// ============================================================================

export interface CustomReportFormData {
  name: string;
  description?: string;
  is_favorite: boolean;
  is_public: boolean;
  folder_id?: string;
  config: ReportConfig;
}

// ============================================================================
// REPORT TEMPLATE TYPES
// ============================================================================

export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  icon?: string;
  config: ReportConfig;
}

// ============================================================================
// QUERY RESULT TYPES
// ============================================================================

export interface ReportQueryResult {
  data: ReportDataPoint[];
  summary?: ReportSummary;
  comparison?: ComparisonResult;
  metadata: ReportMetadata;
}

export interface ReportDataPoint {
  label: string;
  value: number;
  count?: number;
  color?: string;
  drillDownContext?: DrillDownContext;
}

export interface ReportSummary {
  total: number;
  average?: number;
  min?: number;
  max?: number;
  count: number;
}

export interface ComparisonResult {
  current: ReportData;
  previous: ReportData;
  variance: number;
  variancePercent: number;
}

export interface ReportData {
  total: number;
  count: number;
  average?: number;
  data: ReportDataPoint[];
}

export interface ReportMetadata {
  reportId?: string;
  generatedAt: string;
  dataSource: DataSourceType[];
  filters: ReportFilters;
  dateRange: DateRange;
}

export interface DrillDownContext {
  groupBy: GroupByType;
  groupValue: string;
  groupLabel: string;
  dateRange: DateRange;
  filters: ReportFilters;
}

// ============================================================================
// DRILL-DOWN MODAL TYPES
// ============================================================================

export interface DrillDownModalState {
  isOpen: boolean;
  context: DrillDownContext | null;
}

// ============================================================================
// EXPORT TYPES
// ============================================================================

export type ExportFormat = 'pdf' | 'excel' | 'csv';

export interface ExportOptions {
  format: ExportFormat;
  includeMetadata: boolean;
  filename?: string;
}

// ============================================================================
// BUILDER STEP TYPES
// ============================================================================

export type BuilderStep = 1 | 2 | 3;

export interface BuilderState {
  currentStep: BuilderStep;
  config: Partial<ReportConfig>;
  reportName: string;
  reportDescription: string;
  isFavorite: boolean;
  isPublic: boolean;
}

// ============================================================================
// WIZARD VALIDATION SCHEMAS
// ============================================================================

// Step 1: Data Sources & Filters
export const Step1Schema = z.object({
  dataSources: z.array(z.enum(['transactions', 'categories', 'cost_centers', 'clients', 'suppliers'])).min(1, "Select at least one data source"),
  dateRange: DateRangeSchema,
  filters: ReportFiltersSchema,
});

// Step 2: Grouping & Comparison
export const Step2Schema = z.object({
  groupBy: z.enum(['category', 'cost_center', 'client', 'supplier', 'month', 'week']).nullable(),
  compareWith: ComparisonSchema,
});

// Step 3: Visualization & Save
export const Step3Schema = z.object({
  visualizationType: z.enum(['table', 'bar', 'line', 'pie', 'cards']),
  metrics: MetricsSchema,
  chartConfig: ChartConfigSchema.optional(),
  name: z.string().min(1, "Report name is required").max(255, "Report name too long"),
  description: z.string().max(1000, "Description too long").optional(),
  isFavorite: z.boolean().optional(),
  isPublic: z.boolean().optional(),
});

// ============================================================================
// PRE-COMPUTED QUERY HASH TYPE
// ============================================================================

export interface CachedQueryResult {
  data: ReportQueryResult;
  timestamp: number;
  expiresAt: number;
}
